import { CheckCircle2, AlertCircle, Circle } from 'lucide-react';
import type { AppliedPracticeDetailQuestion } from '../services/conceptPlus1Service';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];
const OPTION_KEYS = ['option_a', 'option_b', 'option_c', 'option_d'] as const;

interface Props {
  q: AppliedPracticeDetailQuestion;
  index: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Read-only replay of one Applied Practice question — always reveals the
// solution (no wrong-answer gate; this is retrospective review, not a live
// attempt) and, per design, shows the full worked solution even when the
// question was never attempted, so history doubles as a revision resource.
export default function AppliedPracticeHistoryCard({ q, index }: Props) {
  const attempted = q.option_chosen != null;

  function optionStyle(optionIndex: number): string {
    const base = 'w-full text-left px-4 py-2.5 rounded-lg border text-sm ';
    if (optionIndex === q.correct_option_index) {
      return base + 'border-green-400 bg-green-50 text-green-800 font-medium';
    }
    if (attempted && optionIndex === q.option_chosen) {
      return base + 'border-red-400 bg-red-50 text-red-800';
    }
    return base + 'border-gray-200 text-gray-400';
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-start gap-3">
        <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-coral-warm text-xs font-bold flex items-center justify-center">
          {index + 1}
        </span>
        <div className="flex-1 space-y-3">
          <p className="text-sm text-gray-800 font-medium leading-snug">{q.question}</p>
          {q.context && <p className="text-xs text-gray-500 italic">{q.context}</p>}
          <div className="space-y-2">
            {OPTION_KEYS.map((key, i) => (
              <div key={i} className={optionStyle(i)}>
                <span className="font-semibold mr-2">{OPTION_LABELS[i]}.</span>
                {q[key]}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-3 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2 pt-3">
          {attempted ? (
            q.correct ? (
              <CheckCircle2 size={15} className="text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle size={15} className="text-red-500 flex-shrink-0" />
            )
          ) : (
            <Circle size={15} className="text-gray-400 flex-shrink-0" />
          )}
          <span className={`text-xs font-semibold ${attempted ? (q.correct ? 'text-green-700' : 'text-red-600') : 'text-gray-500'}`}>
            {attempted ? (q.correct ? 'Correct!' : `Incorrect — correct answer is ${OPTION_LABELS[q.correct_option_index]}`) : 'Not attempted yet'}
          </span>
          {q.attempted_at && <span className="ml-auto text-xs text-gray-400">{formatDate(q.attempted_at)}</span>}
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Solution</p>
          <ol className="space-y-1">
            {q.solution_steps.map((step, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700">
                <span className="flex-shrink-0 text-coral-warm font-medium">{i + 1}.</span>
                <span>{step.replace(/^Step\s*\d+[:.]\s*/i, '')}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={15} className="text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-green-800">{q.final_answer}</p>
        </div>
      </div>
    </div>
  );
}
